import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Stack,
  Paper,
  Tabs,
  Tab,
  Button
} from '@mui/material';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useCurrency, formatCurrency } from '../../utils/currency';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#2e7d32', // A premium green color for food/restaurant
    },
    background: {
      default: '#f8fafc',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h5: {
      fontWeight: 700,
    },
    h6: {
      fontWeight: 600,
    }
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
          border: '1px solid #e2e8f0',
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          textTransform: 'none',
          fontSize: '1rem',
        }
      }
    }
  }
});

// Helper to determine chip color based on order status
const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'pending': return 'warning';
    case 'in progress': return 'info';
    case 'ready': return 'primary';
    case 'completed': return 'success';
    case 'cancelled': return 'error';
    default: return 'default';
  }
};

const GuestOrders = () => {
  const { currency } = useCurrency();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const guestId = searchParams.get('guestId');
  const roomId = searchParams.get('roomId');
  const tenantId = searchParams.get('tenantId') || '1'; // Defaulting for demo purposes

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Tab state (0 = Active, 1 = Past)
  const [tabIndex, setTabIndex] = useState(0);

  useEffect(() => {
    let intervalId = null;

    const fetchOrders = async (isBackgroundPoll = false) => {
      if (!guestId) {
        setError('Missing guestId in the URL parameters.');
        if (!isBackgroundPoll) setLoading(false);
        return;
      }

      try {
        if (!isBackgroundPoll) setLoading(true);
        // Using the public endpoint created for this specific Use Case
        const baseUrl = process.env.REACT_APP_API_BASE_URL_PMS || 'http://localhost:3000';
        const response = await axios.get(`${baseUrl}/api/v1/public/restaurant/orders/guest`, {
          params: { guestId },
          headers: { 'x-tenant-id': tenantId }
        });
        
        if (response.data.status === 'success') {
          setOrders(response.data.data || []);
        } else {
          setError('Failed to fetch orders.');
        }
      } catch (err) {
        setError(err.response?.data?.message || 'An error occurred while fetching your orders.');
      } finally {
        if (!isBackgroundPoll) setLoading(false);
      }
    };

    // 1. Initial Data Load
    fetchOrders();

    // 2. Setup Polling Functions
    const startPolling = () => {
      if (!intervalId) {
        intervalId = setInterval(() => {
          fetchOrders(true); // Silent background fetch
        }, 30000); // 30 seconds
      }
    };

    const stopPolling = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    // Start polling right away
    startPolling();

    // 3. Smart Polling: Pause when guest leaves the app/tab
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchOrders(true); // Fetch instantly when they come back
        startPolling();
      } else {
        stopPolling(); // Pause to save battery & DB traffic
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup on unmount
    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [guestId, tenantId]);

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  // Filter orders based on the selected tab
  const filteredOrders = useMemo(() => {
    return orders.filter(order => {
      const isPast = order.status?.toLowerCase() === 'completed' || order.status?.toLowerCase() === 'cancelled';
      return tabIndex === 0 ? !isPast : isPast;
    });
  }, [orders, tabIndex]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', pb: 8 }}>
        {/* Mobile App Header */}
        <Box sx={{ bgcolor: 'primary.main', color: 'white', pt: 6, pb: 4, px: 3, mb: 0, boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ReceiptLongIcon /> My Orders
            </Typography>
            <IconButton color="inherit" onClick={() => navigate(`/?guestId=${guestId}&roomId=${roomId}&tenantId=${tenantId}`)}>
              <RestaurantMenuIcon />
            </IconButton>
          </Box>
          <Typography variant="body1" sx={{ mt: 1, opacity: 0.9 }}>
            Track your delicious orders in real-time.
          </Typography>
        </Box>

        {/* Tabs for Order Types */}
        <Box sx={{ borderBottom: 1, borderColor: '#eaecf0', mb: 4, bgcolor: '#ffffff' }}>
          <Tabs 
            value={tabIndex} 
            onChange={handleTabChange} 
            variant="fullWidth"
          >
            <Tab label="Active Orders" />
            <Tab label="Past Orders" />
          </Tabs>
        </Box>

        <Container maxWidth="sm">
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
          ) : filteredOrders.length === 0 ? (
            <Paper elevation={0} sx={{ p: 4, textAlign: 'center', bgcolor: 'transparent' }}>
              <ReceiptLongIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
              <Typography variant="h6" color="text.secondary">
                {tabIndex === 0 ? 'No active orders' : 'No past orders'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
                {tabIndex === 0 
                  ? 'You currently have no orders being prepared.' 
                  : 'You have no completed or cancelled orders yet.'}
              </Typography>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={() => navigate(`/?guestId=${guestId}&roomId=${roomId}&tenantId=${tenantId}`)}
                sx={{ borderRadius: 8, px: 4, fontWeight: 'bold' }}
              >
                Browse Menu
              </Button>
            </Paper>
          ) : (
            <Stack spacing={3}>
              {filteredOrders.map((order) => (
                <Card key={order.id}>
                  <CardContent sx={{ p: 0 }}>
                    {/* Order Header */}
                    <Box sx={{ p: 2.5, borderBottom: '1px solid #f1f5f9', bgcolor: '#f8fafc', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <AccessTimeIcon sx={{ fontSize: 16 }} />
                          {new Date(order.orderTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                        <Typography variant="h6" sx={{ mt: 0.5 }}>
                          Order #{order.orderId || order.id.substring(0, 8)}
                        </Typography>
                      </Box>
                      <Chip 
                        label={order.status} 
                        color={getStatusColor(order.status)}
                        sx={{ fontWeight: 'bold' }}
                      />
                    </Box>

                    {/* Order Items */}
                    <List sx={{ px: 1, py: 1 }}>
                      {order.orderItems?.map((item, index) => (
                        <React.Fragment key={item.id}>
                          {index > 0 && <Divider component="li" sx={{ mx: 2 }} />}
                          <ListItem>
                            <ListItemText 
                              primary={
                                <Typography variant="body1" fontWeight={500}>
                                  {item.quantity}x {item.menuItem?.name || 'Item'}
                                </Typography>
                              }
                              secondary={`${formatCurrency(parseFloat(item.unitPrice || 0), currency)} each`}
                            />
                            <Typography variant="body1" fontWeight={600}>
                              {formatCurrency(parseFloat(item.totalPrice || 0), currency)}
                            </Typography>
                          </ListItem>
                        </React.Fragment>
                      ))}
                    </List>

                    {/* Order Footer */}
                    <Box sx={{ p: 2.5, borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">Subtotal</Typography>
                        <Typography variant="body2">
                          {formatCurrency(order.orderItems?.reduce((sum, item) => sum + parseFloat(item.totalPrice || 0), 0) || 0, currency)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">Taxes & Fees</Typography>
                        <Typography variant="body2">
                          {formatCurrency(Math.max(0, parseFloat(order.totalAmount || 0) - (order.orderItems?.reduce((sum, item) => sum + parseFloat(item.totalPrice || 0), 0) || 0)), currency)}
                        </Typography>
                      </Box>
                      <Divider sx={{ mb: 2 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          Total Amount
                        </Typography>
                        <Typography variant="h6" color="primary.main" fontWeight="bold">
                          {formatCurrency(parseFloat(order.totalAmount || 0), currency)}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default GuestOrders;
